/**
 * PixelWorldCanvas — React component that hosts the isometric pixel world.
 *
 * Manages canvas lifecycle, engine initialization, sprite loading,
 * scene construction, and the game loop.
 */

import { useRef, useEffect, useCallback, useState } from "react";
import { useWorldStore } from "../../stores/world.js";
import { useTopology, useAgents } from "../../api/hooks.js";
import { usePixelWorldStore } from "../state/PixelWorldStore.js";
import { GameLoop } from "../engine/GameLoop.js";
import { Camera } from "../engine/Camera.js";
import { Renderer } from "../engine/Renderer.js";
import { SpriteFactory } from "../sprites/SpriteFactory.js";
import { gridToWorld } from "../engine/IsoMath.js";
import { createSceneLayout } from "../scenes/SceneFactory.js";
import { buildTileEntities, buildShadowTiles } from "../entities/TileMap.js";
import { Entity } from "../entities/Entity.js";
import { AgentEntity } from "../entities/AgentEntity.js";
import { FurnitureEntity } from "../entities/FurnitureEntity.js";
import { WallEntity } from "../entities/WallEntity.js";
import { EffectEntity } from "../entities/EffectEntity.js";
import { MessageEntity } from "../entities/MessageEntity.js";
import { DemoDriver } from "../state/DemoDriver.js";
import { EventBridge, WsEventSource, createStoreTarget, createEdgeResolver } from "../bridge/index.js";
import type { BridgeContext } from "../bridge/index.js";
import type { AgentAnimState } from "../animation/AnimationController.js";
import type { Sprite } from "../sprites/SpriteSheet.js";
import type { TopologyInfo } from "../scenes/SceneLayout.js";

// Palette imports
import {
  getPaletteForBackend,
  TILE_PALETTE,
  WALL_PALETTE,
  FURNITURE_PALETTE,
  EFFECT_PALETTE,
} from "../sprites/pixel-data/palette.js";

// Sprite data imports
import { ALL_CHARACTER_DEFS } from "../sprites/pixel-data/characters.js";
import {
  TILE_OFFICE_FLOOR,
  TILE_OFFICE_FLOOR_ALT,
  TILE_FACTORY_FLOOR,
  TILE_STAGE_FLOOR,
  TILE_MANAGER_FLOOR,
  TILE_MANAGER_FLOOR_ALT,
  TILE_BREAK_FLOOR,
  TILE_BREAK_FLOOR_ALT,
  TILE_CORRIDOR_FLOOR,
  TILE_CORRIDOR_FLOOR_ALT,
  TILE_FLOOR_SHADOW,
  TILE_GRASS,
  TILE_GRASS_ALT,
  TILE_PATH,
  TILE_PATH_ALT,
} from "../sprites/pixel-data/tiles.js";
import {
  FURNITURE_LARGE_DESK,
  FURNITURE_SMALL_DESK,
  FURNITURE_PLANT,
  FURNITURE_WATER_COOLER,
  FURNITURE_CONVEYOR,
  FURNITURE_JUDGE_BENCH,
  FURNITURE_PODIUM,
  FURNITURE_BOOKSHELF,
  FURNITURE_FILING_CABINET,
  FURNITURE_WHITEBOARD,
  FURNITURE_RUG,
  FURNITURE_COFFEE_MUG,
  FURNITURE_PAPER_STACK,
  FURNITURE_MONITOR_BANK,
  FURNITURE_SERVER_RACK,
  FURNITURE_COMMS_STATION,
  FURNITURE_INBOX_TRAY,
  FURNITURE_BULLETIN_BOARD,
} from "../sprites/pixel-data/furniture.js";
import {
  OUTDOOR_TREE,
  OUTDOOR_BENCH,
  OUTDOOR_BUSH,
  OUTDOOR_FLOWER,
} from "../sprites/pixel-data/outdoor.js";
import {
  WALL_NW,
  WALL_NE,
} from "../sprites/pixel-data/walls.js";
import {
  EFFECT_THOUGHT_BUBBLE,
  EFFECT_SMOKE,
  EFFECT_ZZZ,
  EFFECT_SPARKS,
  EFFECT_EXCLAMATION,
  EFFECT_ENVELOPE_PURPLE,
  EFFECT_ENVELOPE_CYAN,
  EFFECT_ENVELOPE_GREEN,
  EFFECT_ENVELOPE_PINK,
} from "../sprites/pixel-data/effects.js";

/** Map edge type to envelope sprite name. */
const EDGE_ENVELOPE_MAP: Record<string, string> = {
  "delegates-to": "envelope-purple",
  "reports-to": "envelope-cyan",
  "peers-with": "envelope-green",
  "pipes-to": "envelope-pink",
  "custom": "envelope-purple",
};

/** Default demo topology used when no real topology is selected. */
const DEMO_TOPOLOGY: TopologyInfo = {
  type: "star",
  agents: [
    { id: "manager-1", name: "Manager", backend: "claude-agent-sdk" },
    { id: "worker-1", name: "Researcher", backend: "openai-agents" },
    { id: "worker-2", name: "Analyst", backend: "claude-agent-sdk" },
    { id: "worker-3", name: "Writer", backend: "cloudflare-agents" },
    { id: "worker-4", name: "Reviewer", backend: "raw-api" },
  ],
  edges: [
    { from: "manager-1", to: "worker-1", type: "delegates-to", bidirectional: true },
    { from: "manager-1", to: "worker-2", type: "delegates-to", bidirectional: true },
    { from: "manager-1", to: "worker-3", type: "delegates-to", bidirectional: true },
    { from: "manager-1", to: "worker-4", type: "delegates-to", bidirectional: true },
    { from: "worker-1", to: "manager-1", type: "reports-to" },
    { from: "worker-2", to: "manager-1", type: "reports-to" },
    { from: "worker-3", to: "manager-1", type: "reports-to" },
    { from: "worker-4", to: "manager-1", type: "reports-to" },
  ],
  entryPoint: "manager-1",
};

/**
 * Extract TopologyInfo from API data, or fall back to demo topology.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildTopoInfo(topology: any, agents: any[] | undefined): TopologyInfo {
  if (!topology?.definition) return DEMO_TOPOLOGY;

  const def = topology.definition;

  return {
    type: def.type ?? "custom",
    agents: (def.agents ?? []).map((a: any) => {
      const runtime = agents?.find(
        (ra: any) => ra.id === a.id || ra.definition?.id === a.id,
      );
      return {
        id: a.id,
        name: a.name ?? runtime?.definition?.name ?? a.id,
        backend: a.backend ?? runtime?.definition?.backend ?? "raw-api",
      };
    }),
    edges: (def.edges ?? []).map((e: any) => ({
      from: e.from,
      to: e.to,
      type: e.type ?? "custom",
      bidirectional: e.bidirectional,
    })),
    entryPoint: def.entryPoint,
    exitPoints: def.exitPoints,
  };
}

interface PixelWorldCanvasProps {
  /** Override topology info for demo mode. If provided, API hooks are skipped. */
  topologyOverride?: TopologyInfo;
}

export function PixelWorldCanvas({ topologyOverride }: PixelWorldCanvasProps = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<{
    gameLoop: GameLoop;
    camera: Camera;
    renderer: Renderer;
    entities: Entity[];
    agentEntities: Map<string, AgentEntity>;
    effectEntities: EffectEntity[];
    messageEntities: MessageEntity[];
    spriteFactory: SpriteFactory;
    driver: { stop(): void };
  } | null>(null);

  const [loading, setLoading] = useState(true);

  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const { data: topology } = useTopology(selectedTopologyId ?? "");
  const { data: agents } = useAgents(selectedTopologyId ?? undefined);

  // Subscribe to store for reactive updates
  const agentStatuses = usePixelWorldStore((s) => s.agentStatuses);
  const pendingMessages = usePixelWorldStore((s) => s.pendingMessages);
  const dequeueMessage = usePixelWorldStore((s) => s.dequeueMessage);

  // Effect: sync agent statuses to entities
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    for (const [agentId, status] of agentStatuses) {
      const entity = engine.agentEntities.get(agentId);
      if (entity && entity.getStatus() !== status) {
        entity.setStatus(status as AgentAnimState);
      }
    }
  }, [agentStatuses]);

  // Effect: spawn message animations from pending messages
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;

    for (const msg of pendingMessages) {
      const fromEntity = engine.agentEntities.get(msg.fromAgentId);
      const toEntity = engine.agentEntities.get(msg.toAgentId);
      if (!fromEntity || !toEntity) {
        dequeueMessage(msg.id);
        continue;
      }

      const envelopeKey = EDGE_ENVELOPE_MAP[msg.edgeType] ?? "envelope-purple";
      const sprite = engine.spriteFactory.get(envelopeKey);
      if (!sprite) {
        dequeueMessage(msg.id);
        continue;
      }

      const messageEntity = new MessageEntity(
        msg.id,
        sprite,
        { worldX: fromEntity.worldX, worldY: fromEntity.worldY - 10 },
        { worldX: toEntity.worldX, worldY: toEntity.worldY - 10 },
      );
      engine.messageEntities.push(messageEntity);
      dequeueMessage(msg.id);
    }
  }, [pendingMessages, dequeueMessage]);

  // Main initialization effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let destroyed = false;

    async function init() {
      // Build topology info for scene layout
      const topoInfo = topologyOverride ?? buildTopoInfo(topology, agents);
      if (!topoInfo || !canvas) return;

      // Handle DPI scaling
      const resizeCanvas = () => {
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.scale(dpr, dpr);
          ctx.imageSmoothingEnabled = false;
        }
      };
      resizeCanvas();

      const camera = new Camera();
      const renderer = new Renderer(canvas, camera);
      const spriteFactory = new SpriteFactory();

      // ---- Build all sprites ----

      // Tiles
      await spriteFactory.build(TILE_OFFICE_FLOOR, TILE_PALETTE, "tile-office");
      await spriteFactory.build(TILE_OFFICE_FLOOR_ALT, TILE_PALETTE, "tile-office-alt");
      await spriteFactory.build(TILE_FACTORY_FLOOR, TILE_PALETTE, "tile-factory");
      await spriteFactory.build(TILE_STAGE_FLOOR, TILE_PALETTE, "tile-stage");
      await spriteFactory.build(TILE_MANAGER_FLOOR, TILE_PALETTE, "tile-manager");
      await spriteFactory.build(TILE_MANAGER_FLOOR_ALT, TILE_PALETTE, "tile-manager-alt");
      await spriteFactory.build(TILE_BREAK_FLOOR, TILE_PALETTE, "tile-break");
      await spriteFactory.build(TILE_BREAK_FLOOR_ALT, TILE_PALETTE, "tile-break-alt");
      await spriteFactory.build(TILE_CORRIDOR_FLOOR, TILE_PALETTE, "tile-corridor");
      await spriteFactory.build(TILE_CORRIDOR_FLOOR_ALT, TILE_PALETTE, "tile-corridor-alt");
      await spriteFactory.build(TILE_FLOOR_SHADOW, TILE_PALETTE, "tile-shadow");
      await spriteFactory.build(TILE_GRASS, TILE_PALETTE, "tile-grass");
      await spriteFactory.build(TILE_GRASS_ALT, TILE_PALETTE, "tile-grass-alt");
      await spriteFactory.build(TILE_PATH, TILE_PALETTE, "tile-path");
      await spriteFactory.build(TILE_PATH_ALT, TILE_PALETTE, "tile-path-alt");

      // Walls
      await spriteFactory.build(WALL_NW, WALL_PALETTE, "wall-nw");
      await spriteFactory.build(WALL_NE, WALL_PALETTE, "wall-ne");

      // Furniture
      await spriteFactory.build(FURNITURE_LARGE_DESK, FURNITURE_PALETTE, "desk-large");
      await spriteFactory.build(FURNITURE_SMALL_DESK, FURNITURE_PALETTE, "desk-small");
      await spriteFactory.build(FURNITURE_PLANT, FURNITURE_PALETTE, "plant");
      await spriteFactory.build(FURNITURE_WATER_COOLER, FURNITURE_PALETTE, "water-cooler");
      await spriteFactory.build(FURNITURE_CONVEYOR, FURNITURE_PALETTE, "conveyor");
      await spriteFactory.build(FURNITURE_JUDGE_BENCH, FURNITURE_PALETTE, "judge-bench");
      await spriteFactory.build(FURNITURE_PODIUM, FURNITURE_PALETTE, "podium");
      await spriteFactory.build(FURNITURE_BOOKSHELF, FURNITURE_PALETTE, "bookshelf");
      await spriteFactory.build(FURNITURE_FILING_CABINET, FURNITURE_PALETTE, "filing-cabinet");
      await spriteFactory.build(FURNITURE_WHITEBOARD, FURNITURE_PALETTE, "whiteboard");
      await spriteFactory.build(FURNITURE_RUG, FURNITURE_PALETTE, "rug");
      await spriteFactory.build(FURNITURE_COFFEE_MUG, FURNITURE_PALETTE, "coffee-mug");
      await spriteFactory.build(FURNITURE_PAPER_STACK, FURNITURE_PALETTE, "paper-stack");
      await spriteFactory.build(FURNITURE_MONITOR_BANK, FURNITURE_PALETTE, "monitor-bank");
      await spriteFactory.build(FURNITURE_SERVER_RACK, FURNITURE_PALETTE, "server-rack");
      await spriteFactory.build(FURNITURE_COMMS_STATION, FURNITURE_PALETTE, "comms-station");
      await spriteFactory.build(FURNITURE_INBOX_TRAY, FURNITURE_PALETTE, "inbox-tray");
      await spriteFactory.build(FURNITURE_BULLETIN_BOARD, FURNITURE_PALETTE, "bulletin-board");

      // Outdoor decorations
      await spriteFactory.build(OUTDOOR_TREE, FURNITURE_PALETTE, "tree");
      await spriteFactory.build(OUTDOOR_BENCH, FURNITURE_PALETTE, "bench");
      await spriteFactory.build(OUTDOOR_BUSH, FURNITURE_PALETTE, "bush");
      await spriteFactory.build(OUTDOOR_FLOWER, FURNITURE_PALETTE, "flower");

      // Effects
      await spriteFactory.build(EFFECT_THOUGHT_BUBBLE, EFFECT_PALETTE, "thought-bubble");
      await spriteFactory.build(EFFECT_SMOKE, EFFECT_PALETTE, "smoke");
      await spriteFactory.build(EFFECT_ZZZ, EFFECT_PALETTE, "zzz");
      await spriteFactory.build(EFFECT_SPARKS, EFFECT_PALETTE, "sparks");
      await spriteFactory.build(EFFECT_EXCLAMATION, EFFECT_PALETTE, "exclamation");
      await spriteFactory.build(EFFECT_ENVELOPE_PURPLE, EFFECT_PALETTE, "envelope-purple");
      await spriteFactory.build(EFFECT_ENVELOPE_CYAN, EFFECT_PALETTE, "envelope-cyan");
      await spriteFactory.build(EFFECT_ENVELOPE_GREEN, EFFECT_PALETTE, "envelope-green");
      await spriteFactory.build(EFFECT_ENVELOPE_PINK, EFFECT_PALETTE, "envelope-pink");

      // Character sprites — build for each unique backend in the topology
      const backends = new Set(topoInfo.agents.map((a) => a.backend ?? "raw-api"));
      for (const backend of backends) {
        const palette = getPaletteForBackend(backend);
        for (const [state, def] of Object.entries(ALL_CHARACTER_DEFS)) {
          await spriteFactory.build(def, palette, `character-${state}-${backend}`);
        }
      }

      if (destroyed) return;

      // ---- Build scene layout ----
      const layout = createSceneLayout(topoInfo);

      // ---- Create entities ----
      const entities: Entity[] = [];
      const agentEntities = new Map<string, AgentEntity>();
      const effectEntities: EffectEntity[] = [];
      const messageEntities: MessageEntity[] = [];

      // Floor tiles
      const floorAreas = layout.floors.map((f) => {
        let tileSprite: Sprite;
        let altTileSprite: Sprite | undefined;
        switch (f.tileType) {
          case "factory":
            tileSprite = spriteFactory.get("tile-factory")!;
            break;
          case "stage":
            tileSprite = spriteFactory.get("tile-stage")!;
            break;
          case "manager":
            tileSprite = spriteFactory.get("tile-manager")!;
            altTileSprite = spriteFactory.get("tile-manager-alt") ?? undefined;
            break;
          case "break":
            tileSprite = spriteFactory.get("tile-break")!;
            altTileSprite = spriteFactory.get("tile-break-alt") ?? undefined;
            break;
          case "grass":
            tileSprite = spriteFactory.get("tile-grass")!;
            altTileSprite = spriteFactory.get("tile-grass-alt") ?? undefined;
            break;
          case "path":
            tileSprite = spriteFactory.get("tile-path")!;
            altTileSprite = spriteFactory.get("tile-path-alt") ?? undefined;
            break;
          default: // "office"
            tileSprite = spriteFactory.get("tile-office")!;
            altTileSprite = spriteFactory.get("tile-office-alt") ?? undefined;
            break;
        }
        return {
          tileSprite,
          altTileSprite,
          startCol: f.startCol,
          startRow: f.startRow,
          width: f.width,
          height: f.height,
        };
      });
      const tileEntities = buildTileEntities(floorAreas);
      entities.push(...tileEntities);

      // Shadow tiles removed — clean cutaway style without perimeter shadows

      // Walls
      for (const w of layout.walls) {
        const wallSprite = spriteFactory.get(w.side === "nw" ? "wall-nw" : "wall-ne");
        if (wallSprite) {
          entities.push(new WallEntity(`wall-${w.side}-${w.gridCol}-${w.gridRow}`, w.gridCol, w.gridRow, wallSprite));
        }
      }

      // Furniture
      for (const f of layout.furniture) {
        const sprite = spriteFactory.get(f.type);
        if (sprite) {
          // Rugs should be at layer 0 (flat on floor), not layer 1
          const layer = f.type === "rug" ? 0 : 1;
          entities.push(new FurnitureEntity(`furn-${f.type}-${f.gridCol}-${f.gridRow}`, f.gridCol, f.gridRow, sprite, layer));
        }
      }

      // Desks (placed at agent positions, layer 1)
      for (const ap of layout.agents) {
        const deskSprite = spriteFactory.get(ap.deskType === "large" ? "desk-large" : "desk-small");
        if (deskSprite) {
          entities.push(new FurnitureEntity(`desk-${ap.agentId}`, ap.gridCol, ap.gridRow, deskSprite));
        }
      }

      // Agent characters
      for (const ap of layout.agents) {
        const agentInfo = topoInfo.agents.find((a) => a.id === ap.agentId);
        const backend = agentInfo?.backend ?? "raw-api";
        const name = agentInfo?.name ?? ap.agentId.slice(0, 8);

        // Build sprite record for AnimationController
        const sprites: Record<AgentAnimState, Sprite> = {
          idle: spriteFactory.get(`character-idle-${backend}`)!,
          running: spriteFactory.get(`character-running-${backend}`)!,
          waiting: spriteFactory.get(`character-waiting-${backend}`)!,
          error: spriteFactory.get(`character-error-${backend}`)!,
          terminated: spriteFactory.get(`character-terminated-${backend}`)!,
        };

        const agent = new AgentEntity(
          ap.agentId,
          name,
          backend,
          ap.gridCol,
          ap.gridRow,
          ap.isEntryPoint,
          sprites,
        );

        // Wire up effect spawning
        agent.onSpawnEffect = (type, wx, wy) => {
          const effectSprite = spriteFactory.get(type);
          if (!effectSprite) return;
          const effect = new EffectEntity(
            `fx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            effectSprite,
            wx,
            wy,
          );
          effectEntities.push(effect);
        };

        agentEntities.set(ap.agentId, agent);
        entities.push(agent);
      }

      // ---- Fit camera to scene ----
      const bounds = layout.bounds;
      const c1 = gridToWorld(bounds.minCol, bounds.minRow);
      const c2 = gridToWorld(bounds.minCol, bounds.maxRow);
      const c3 = gridToWorld(bounds.maxCol, bounds.minRow);
      const c4 = gridToWorld(bounds.maxCol, bounds.maxRow);
      const worldMinX = Math.min(c1.x, c2.x, c3.x, c4.x);
      const worldMaxX = Math.max(c1.x, c2.x, c3.x, c4.x);
      const worldMinY = Math.min(c1.y, c2.y, c3.y, c4.y);
      const worldMaxY = Math.max(c1.y, c2.y, c3.y, c4.y);
      const rect = canvas.getBoundingClientRect();
      camera.fitToScene(
        { minX: worldMinX - 20, minY: worldMinY - 40, maxX: worldMaxX + 20, maxY: worldMaxY + 20 },
        rect.width,
        rect.height,
      );

      // ---- Game loop ----
      const gameLoop = new GameLoop({
        update(dt) {
          // Update all static entities
          for (const e of entities) e.update(dt);

          // Update effects
          for (const e of effectEntities) e.update(dt);
          // Remove expired effects
          for (let i = effectEntities.length - 1; i >= 0; i--) {
            if (effectEntities[i].expired) effectEntities.splice(i, 1);
          }

          // Update messages
          for (const m of messageEntities) m.update(dt);
          // Remove completed messages
          for (let i = messageEntities.length - 1; i >= 0; i--) {
            if (messageEntities[i].completed) messageEntities.splice(i, 1);
          }
        },
        render() {
          // Combine all entity arrays for rendering
          const allEntities: Entity[] = [
            ...entities,
            ...effectEntities,
            ...messageEntities,
          ];
          renderer.render(allEntities);
        },
      });

      // ---- Driver: live event bridge when a real topology is selected, else demo ----
      const target = createStoreTarget();
      const agentIds = [...agentEntities.keys()];

      let driver: { stop(): void };
      if (!topologyOverride && selectedTopologyId && topology?.definition) {
        const ctx: BridgeContext = {
          topologyId: selectedTopologyId,
          topologyType: topoInfo.type,
          resolveEdgeType: createEdgeResolver(topoInfo),
          hasAgent: (id) => agentEntities.has(id),
        };
        const bridge = new EventBridge(new WsEventSource());
        bridge.connect(target, ctx); // calls target.reset() + setScene() internally
        driver = { stop: () => bridge.disconnect() };
      } else {
        usePixelWorldStore.getState().reset();
        const demo = new DemoDriver();
        demo.start(target, agentIds);
        driver = { stop: () => demo.stop() };
      }

      // Race guard: cleanup may have run while async init was in flight.
      if (destroyed) {
        driver.stop();
        return;
      }

      // ---- Store reference ----
      engineRef.current = {
        gameLoop,
        camera,
        renderer,
        entities,
        agentEntities,
        effectEntities,
        messageEntities,
        spriteFactory,
        driver,
      };

      // ---- Resize observer ----
      const observer = new ResizeObserver(() => {
        if (destroyed) return;
        resizeCanvas();
        renderer.disableSmoothing();
      });
      observer.observe(canvas);

      gameLoop.start();
      setLoading(false);

      // Cleanup function stored for the effect teardown
      return () => {
        observer.disconnect();
      };
    }

    let cleanupObserver: (() => void) | undefined;
    init().then((cleanup) => {
      cleanupObserver = cleanup;
    });

    return () => {
      destroyed = true;
      if (engineRef.current) {
        engineRef.current.gameLoop.stop();
        engineRef.current.driver.stop();
        engineRef.current.spriteFactory.clear();
        engineRef.current = null;
      }
      cleanupObserver?.();
    };
  }, [topology, agents, topologyOverride, selectedTopologyId]);

  // ---- Mouse handlers for pan/zoom ----
  const isPanning = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isPanning.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current || !engineRef.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    engineRef.current.camera.pan(dx, dy);
    lastMouse.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseUp = useCallback(() => {
    isPanning.current = false;
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!engineRef.current || !canvasRef.current) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    engineRef.current.camera.zoomAt(
      e.clientX - rect.left,
      e.clientY - rect.top,
      e.deltaY,
      rect.width,
      rect.height,
    );
  }, []);

  return (
    <div className="relative w-full h-full bg-[#1a2a15]">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="text-text-secondary text-sm animate-pulse">Loading pixel world...</div>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        style={{ imageRendering: "pixelated" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      />
    </div>
  );
}
